import { useEffect, useState } from "react";
import { productManager } from "../../data/managers/ProductManager";
import { Category, Product } from "../../data/models/product/ProductModel";
import { useLoading } from "../../data/providers/LoadingProvider";
import { useSnackbar } from "../../data/providers/SnackbarProvider";
import { useStations } from "./useStations";

export const useProducts = ({ requestHard = false } = {}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const { showSnackbar, showError } = useSnackbar();
  const { startLoading, stopLoading, loading } = useLoading();

  const {
    stations,
    createStation,
    deleteStation,
    refreshStations,
    loading: loadingStations,
  } = useStations();

  useEffect(() => {
    refreshCategories();
    refreshProducts();
  }, []);

  const refreshCategories = async () => {
    try {
      startLoading();
      const resp = await productManager.getAllCategories();
      if (!resp) {
        showError("No se pudieron cargar las categorías");
        return;
      }
      setCategories(resp.map((c: any) => new Category(c)));
    } catch {
      showError("Error al obtener categorías");
    } finally {
      stopLoading();
    }
  };

  const createCategory = async (name: string) => {
    try {
      startLoading();
      const r = await productManager.updateOrCreateCategory({ name });
      if (!r) throw new Error();
      showSnackbar("Categoría creada correctamente");
    } catch (err) {
      showError(`No se pudo crear la categoría. ${err}`);
    } finally {
      await refreshCategories();
      stopLoading();
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      startLoading();
      const cat = categories.find((c) => c.id === id);
      if (!cat) throw new Error("Category not found");
      const r = await productManager.deleteCategory(cat);
      if (!r) throw new Error();
      showSnackbar("Categoría eliminada");
    } catch {
      showError("No se pudo eliminar la categoría");
    } finally {
      await refreshCategories();
      stopLoading();
    }
  };

  const refreshProducts = async () => {
    try {
      startLoading();
      const resp = await productManager.getAllProducts();
      if (!resp) {
        showError("No se pudieron cargar los productos");
        return;
      }
      setProducts(resp.map((p: any) => new Product(p)));
    } catch {
      showError("Error al obtener productos");
    } finally {
      stopLoading();
    }
  };

  const createProduct = async (data: Omit<Product, "id">) => {
    try {
      startLoading();
      const r = await productManager.updateOrCreateProduct(data);
      if (!r) throw new Error();
      showSnackbar("Producto creado exitosamente");
    } catch {
      showError("No se pudo crear el producto");
    } finally {
      await refreshProducts();
      stopLoading();
    }
  };

  const updateProduct = async (
    id: string,
    data: Partial<Omit<Product, "id">>
  ) => {
    try {
      startLoading();
      const existing = products.find((p) => p.id === id);
      if (!existing) throw new Error("Product not found");
      const updated = { ...existing, ...data };
      const r = await productManager.updateOrCreateProduct(updated);
      if (!r) throw new Error();
      showSnackbar("Producto actualizado correctamente");
    } catch {
      showError("No se pudo actualizar el producto");
    } finally {
      await refreshProducts();
      stopLoading();
    }
  };

  const deleteProduct = async (id: string) => {
    try {
      startLoading();
      const prod = products.find((p) => p.id === id);
      if (!prod) throw new Error("Product not found");
      const r = await productManager.deleteProduct(prod);
      if (!r) throw new Error();
      showSnackbar("Producto eliminado");
    } catch {
      showError("No se pudo eliminar el producto");
    } finally {
      await refreshProducts();
      stopLoading();
    }
  };

  return {
    categories,
    products,
    stations,
    totalCategories: categories.length,
    totalProducts: products.length,
    totalStations: stations.length,
    createCategory,
    deleteCategory,
    createProduct,
    updateProduct,
    deleteProduct,
    createStation,
    deleteStation,
    refreshProducts,
    refreshStations,
    loading,
    loadingStations,
  };
};
